import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcGenerateV1MonthlyStatementsTaskQueryServiceMonthlyStatementsTaskGet } from '@interxfi/api-client/generated/hooks/ixc-generate-v1-monthly-statements-task-query-service-monthly-statements-task-get';
import { TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import DownloadIcon from '@interxfi/design-system/icons/react/icon-download';
import type { IxcGenerateV1MonthlyStatementsTaskState } from '@interxfi/types/generated/api';
import { useDownloadDocument } from '../../../../../../../../../../../features/document/utils';
import {
  DocgenMonthlyStatementTaskEventDrawer,
  DocgenMonthlyStatementTaskEventSummary,
} from '../../../../../../../../../../../features/generate/monthly-statement/task/docgen-monthly-statement-task-event';
import { getDocgenMonthlyStatementTaskStateListItems } from '../../../../../../../../../../../features/generate/monthly-statement/task/docgen-monthly-statement-task-list-items';
import { getDocgenMonthlyStatementTaskDetailPath } from '../../../../../../../../../../../features/generate/monthly-statement/task/docgen-monthly-statement-task-routing';
import { MonthlyStatementTaskStatusPill } from '../../../../../../../../../../../features/generate/monthly-statement/task/monthly-statement-task-status-pill';
import { ProgramLogomark } from '../../../../../../../../../../../features/userauth/program/logomark/program-logomark';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/generate/monthly-statement/task/_task/$taskId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `monthly-statement-task-${params.taskId}`,
        label: 'Task',
        to: getDocgenMonthlyStatementTaskDetailPath(params.taskId),
        suffix: <CopyButton content={params.taskId} label="Copy Task ID" tooltip={{ copy: params.taskId, copied: 'Copied Task ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Monthly Statement Task'),
  component: DocgenMonthlyStatementTaskDetail,
});

function getListItems(data: IxcGenerateV1MonthlyStatementsTaskState | undefined) {
  const items = getDocgenMonthlyStatementTaskStateListItems(data);

  return {
    general: {
      left: [
        items.data.spec.startDate,
        items.data.spec.endDate,
        items.data.spec.revision,
        ...(items.data.documentDetails
          ? [items.data.documentDetails.documentIdWithActions, items.data.documentDetails.bucket, items.data.documentDetails.key]
          : []),
      ],
      right: [items.metadata.createdAt, items.metadata.updatedAt, items.taskId, items.jobId, items.accountId, items.programId],
    },
  };
}

function getMenuItems(
  taskId: string,
  documentId: string | undefined,
  handleDownload: (docId: string) => Promise<void>,
  isDownloading: boolean,
): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: DocgenMonthlyStatementTaskEventDrawer,
        props: { request: { taskId } },
      },
    },
    actions: documentId
      ? [
          {
            id: 'download',
            icon: <DownloadIcon aria-hidden />,
            label: 'Download',
            disabled: isDownloading,
            onClick: async () => {
              try {
                await handleDownload(documentId);
              } catch {}
            },
          },
        ]
      : [],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load monthly statement task', 'view this monthly statement task');
const downloadErrorMessageGetter = buildDefaultErrorMessageGetter(
  'Failed to download monthly statement document',
  'view this monthly statement document',
);

function DocgenMonthlyStatementTaskDetail() {
  const { taskId } = Route.useParams();
  const request = { taskId } as const;
  const { data, error, isPending } = useIxcGenerateV1MonthlyStatementsTaskQueryServiceMonthlyStatementsTaskGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.monthlyStatementsTask), [data?.monthlyStatementsTask]);
  const { handleDownload, isPending: isDownloading, error: downloadError } = useDownloadDocument();
  useErrorHandler(downloadError, downloadErrorMessageGetter);
  const menuItems = useMemo(
    () => getMenuItems(taskId, data?.monthlyStatementsTask?.data?.spec?.details?.documentId, handleDownload, isDownloading),
    [taskId, data?.monthlyStatementsTask?.data?.spec?.details?.documentId, handleDownload, isDownloading],
  );
  const { activeTenantContext } = useActiveRealmAPIContext();

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            {activeTenantContext !== TenantContext.Program ? (
              <ProgramLogomark programId={data?.monthlyStatementsTask?.programId} shouldLoadLogoMark size="large" withTooltip />
            ) : null}
            <MonthlyStatementTaskStatusPill isPending={showPending} size="large" status={data?.monthlyStatementsTask?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <DocgenMonthlyStatementTaskEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <AppReadonlyList isPending={showPending} items={listItems.general.left} />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
